import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Avatar, Button, Input, Skeleton, Tooltip } from "antd";
import {
  AimOutlined,
  CheckCircleFilled,
  CopyOutlined,
  DownOutlined,
  EnvironmentOutlined,
  GlobalOutlined,
  LockOutlined,
  LoginOutlined,
  PhoneFilled,
  RightOutlined,
  SearchOutlined,
  ShareAltOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import useUser from "../../hooks/useUser";
import useData from "../../datas/useData";
import { useSession } from "../../lib/session";
import AppShell, { BloodDropIcon } from "../../components/app-shell/AppShell";
import Sheet from "../../components/app-shell/Sheet";
import "./blood.css";

const PAGE_SIZE = 20;
const DONATION_GAP_DAYS = 90;
const ALL = "all";

// The upazila list uses the old spelling for these districts.
const UPAZILA_DISTRICT_ALIAS = {
  barishal: "barisal",
  bogura: "bogra",
  chattogram: "chittagong",
  cumilla: "comilla",
  jashore: "jessore",
};

// Which donor groups a patient of each group can receive (red cells).
const COMPATIBLE_DONORS = {
  "A+": ["A+", "A-", "O+", "O-"],
  "A-": ["A-", "O-"],
  "B+": ["B+", "B-", "O+", "O-"],
  "B-": ["B-", "O-"],
  "AB+": ["AB+", "AB-", "A+", "A-", "B+", "B-", "O+", "O-"],
  "AB-": ["AB-", "A-", "B-", "O-"],
  "O+": ["O+", "O-"],
  "O-": ["O-"],
};

// Donor counts are keyed by the current spelling, lowercased.
const districtKey = (name) => {
  const lower = String(name || "").trim().toLowerCase();
  const current = Object.keys(UPAZILA_DISTRICT_ALIAS).find((key) => UPAZILA_DISTRICT_ALIAS[key] === lower);
  return current || lower;
};

const stripUpazila = (name) => String(name || "").replace(/\s*Upazila\s*$/i, "").trim();
const daysSince = (date) => Math.floor((Date.now() - new Date(date).getTime()) / (1000 * 3600 * 24));
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

const copyNumber = async (phone) => {
  try {
    await navigator.clipboard.writeText(phone);
    toast.success("Number copied");
  } catch (error) {
    toast.error("Could not copy the number");
  }
};

/* ---------------- Pieces ---------------- */

const StepHeader = ({ step, blood, title, hint }) => (
  <div className="bs-step-head">
    <div className="bs-progress" aria-label={`Step ${step} of 3`}>
      {[1, 2, 3].map((n) => (
        <span key={n} className={`bs-progress__dot ${n <= step ? "bs-progress__dot--on" : ""}`} />
      ))}
    </div>
    {blood ? <span className="bs-step-head__group">{blood}</span> : null}
    <h1 className="bs-step-head__title">{title}</h1>
    {hint ? <p className="bs-step-head__hint">{hint}</p> : null}
  </div>
);

const GroupGrid = ({ bloods, counts, selected, onSelect }) => (
  <div className="bs-groups" role="radiogroup" aria-label="Blood group">
    {bloods.map(({ value }) => (
      <button
        key={value}
        type="button"
        role="radio"
        aria-checked={selected === value}
        className={`bs-circle ${selected === value ? "bs-circle--active" : ""}`}
        onClick={() => onSelect(value)}
      >
        <span className="bs-circle__group">{value}</span>
        {counts ? <span className="bs-circle__count">{counts[value] ?? 0}</span> : null}
      </button>
    ))}
  </div>
);

const PlaceRow = ({ icon, title, sub, count, onClick }) => (
  <button type="button" className={`app-list-item ${count === 0 ? "bs-place--empty" : ""}`} onClick={onClick}>
    {icon ? <span className="bs-place__icon">{icon}</span> : null}
    <span className="app-list-item__main">
      <span className="app-list-item__title">{title}</span>
      {sub ? <span className="app-list-item__sub">{sub}</span> : null}
    </span>
    {count != null ? <span className="bs-place__count">{count}</span> : null}
    <RightOutlined className="app-list-item__end" />
  </button>
);

const DonationStatus = ({ donor }) => {
  if (!donor.lastDonation) {
    return <span className="bs-status bs-status--ready">Ready to donate</span>;
  }
  const days = daysSince(donor.lastDonation);
  if (donor.canDonate) {
    return <span className="bs-status bs-status--ready">Ready · last gave {days} days ago</span>;
  }
  return (
    <span className="bs-status bs-status--wait">
      Gave {days} days ago · ready in {DONATION_GAP_DAYS - days} days
    </span>
  );
};

const DonorCard = ({ donor, loggedIn, onLogin }) => {
  const location = [stripUpazila(donor.upuzila), donor.district].filter(Boolean).join(", ");
  return (
    <div className={`bs-card ${donor.canDonate ? "" : "bs-card--muted"}`}>
      <div className="bs-card__head">
        <Avatar size={48} src={donor.profilePhoto || undefined} icon={<UserOutlined />} />
        <div className="bs-card__who">
          <div className="bs-card__name">{donor.name || "Unnamed donor"}</div>
          {location && (
            <div className="bs-card__loc">
              <EnvironmentOutlined /> {location}
            </div>
          )}
        </div>
        <div className="bs-card__group">{donor.blood}</div>
      </div>

      <DonationStatus donor={donor} />

      {donor.contactPublic && donor.phones.length > 0 ? (
        <div className="bs-card__phones">
          {donor.phones.map((phone) => (
            <div key={phone} className="bs-phone">
              <a className="bs-phone__call" href={`tel:${phone}`}>
                <PhoneFilled /> {phone}
              </a>
              <Tooltip title="Copy number">
                <Button
                  className="bs-phone__copy"
                  icon={<CopyOutlined />}
                  aria-label={`Copy ${phone}`}
                  onClick={() => copyNumber(phone)}
                />
              </Tooltip>
            </div>
          ))}
        </div>
      ) : donor.contactPublic && !loggedIn ? (
        <button type="button" className="bs-card__login" onClick={onLogin}>
          <LoginOutlined /> Log in to see number
        </button>
      ) : (
        <div className="bs-card__private">
          <LockOutlined /> This donor keeps their number private
        </div>
      )}
    </div>
  );
};

/* ---------------- Screen ---------------- */

// Steps live in the URL (each one a history entry, so the phone's back
// button steps back), which also makes any search shareable:
//   ?blood=B%2B                                -> choose district
//   ?blood=B%2B&district=Dhaka                 -> choose upazila
//   ?blood=B%2B&district=Dhaka&upuzila=Savar   -> results
// district=all / upuzila=all mean "anywhere" / "whole district".
const BloodSearch = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { endUser, openLogin } = useSession();
  const { searchBloodDonors } = useUser();
  const { bloods, districts, upuzilas } = useData();
  const [placeQuery, setPlaceQuery] = useState("");
  const [groupSheetOpen, setGroupSheetOpen] = useState(false);

  const blood = COMPATIBLE_DONORS[searchParams.get("blood")] ? searchParams.get("blood") : "";
  const district = searchParams.get("district") || "";
  const upuzila = searchParams.get("upuzila") || "";
  const compatible = searchParams.get("compat") === "1";
  const readyOnly = searchParams.get("ready") === "1";

  const step = !blood
    ? "group"
    : !district
    ? "district"
    : district !== ALL && !upuzila
    ? "upazila"
    : "results";

  const go = (changes, { replace = false } = {}) => {
    const next = {
      blood,
      district,
      upuzila,
      compat: compatible ? "1" : "",
      ready: readyOnly ? "1" : "",
      ...changes,
    };
    setPlaceQuery("");
    setSearchParams(
      Object.fromEntries(Object.entries(next).filter(([, value]) => value)),
      { replace }
    );
    if (!replace) window.scrollTo({ top: 0 });
  };

  const apiDistrict = district === ALL ? "" : district;
  const apiUpazila = upuzila === ALL ? "" : upuzila;
  const userKey = endUser?._id || null;

  // Counts for the group circles and the place lists.
  const { data: overview } = useQuery({
    queryKey: ["blood-overview", step, blood, apiDistrict, compatible],
    queryFn: () =>
      searchBloodDonors({
        blood: step === "group" ? "" : blood,
        district: step === "upazila" ? apiDistrict : "",
        compatible,
        breakdown: true,
        limit: 6,
      }),
    enabled: step !== "results",
    placeholderData: keepPreviousData,
  });

  const results = useInfiniteQuery({
    queryKey: ["blood-results", blood, apiDistrict, apiUpazila, compatible, readyOnly, userKey],
    queryFn: ({ pageParam }) =>
      searchBloodDonors({
        blood,
        district: apiDistrict,
        upuzila: apiUpazila,
        compatible,
        readyOnly,
        page: pageParam,
        limit: PAGE_SIZE,
      }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page < last.totalPages ? last.page + 1 : undefined),
    enabled: step === "results",
  });

  const areaCounts = overview?.areaCounts || {};
  const areaTotal = Object.values(areaCounts).reduce((sum, n) => sum + n, 0);

  const districtRows = useMemo(() => {
    const q = placeQuery.trim().toLowerCase();
    return districts
      .map((d) => d.name)
      .filter((name) => !q || name.toLowerCase().includes(q))
      .sort((a, b) => a.localeCompare(b));
  }, [districts, placeQuery]);

  const upazilaRows = useMemo(() => {
    if (!apiDistrict) return [];
    const lower = apiDistrict.toLowerCase();
    const listName = UPAZILA_DISTRICT_ALIAS[lower] || lower;
    const q = placeQuery.trim().toLowerCase();
    return upuzilas
      .filter((x) => String(x.district).toLowerCase() === listName)
      .map((x) => stripUpazila(x.upazila))
      .filter((name) => !q || name.toLowerCase().includes(q))
      .sort((a, b) => a.localeCompare(b));
  }, [apiDistrict, upuzilas, placeQuery]);

  const shareSearch = async () => {
    const url = window.location.href;
    const where = apiUpazila ? `${apiUpazila}, ${apiDistrict}` : apiDistrict || "Bangladesh";
    const text = `Looking for ${blood} blood donors in ${where} on 0709 Bangladesh`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Find blood donor", text, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied");
      }
    } catch (error) {
      // The share sheet was dismissed.
    }
  };

  const placeSearch = (placeholder) => (
    <Input
      className="bs-place-search app-search"
      size="large"
      allowClear
      prefix={<SearchOutlined />}
      placeholder={placeholder}
      value={placeQuery}
      onChange={(e) => setPlaceQuery(e.target.value)}
    />
  );

  /* ----- Step 1: blood group ----- */
  if (step === "group") {
    return (
      <AppShell title="Find blood">
        <StepHeader step={1} title="Which blood group is needed?" hint="The number under each group is how many donors we have." />
        <GroupGrid bloods={bloods} counts={overview?.counts} onSelect={(value) => go({ blood: value })} />
        <p className="bs-footnote">
          <BloodDropIcon /> Donors can give again {DONATION_GAP_DAYS} days after their last donation.
        </p>
      </AppShell>
    );
  }

  /* ----- Step 2: district ----- */
  if (step === "district") {
    const myDistrict = endUser?.district;
    return (
      <AppShell title="Find blood" back>
        <StepHeader step={2} blood={blood} title="Where is it needed?" hint="Pick the district of the hospital." />
        {placeSearch("Search district")}
        {!placeQuery ? (
          <div className="app-list bs-place-list">
            {myDistrict ? (
              <PlaceRow
                icon={<AimOutlined />}
                title={`My district · ${myDistrict}`}
                count={overview ? areaCounts[districtKey(myDistrict)] || 0 : null}
                onClick={() => go({ district: myDistrict })}
              />
            ) : null}
            <PlaceRow
              icon={<GlobalOutlined />}
              title="Anywhere in Bangladesh"
              sub="Show every donor"
              count={overview ? areaTotal : null}
              onClick={() => go({ district: ALL })}
            />
          </div>
        ) : null}
        <div className="app-section-title">Districts</div>
        <div className="app-list">
          {districtRows.length === 0 ? (
            <div className="app-empty">No district matches "{placeQuery}"</div>
          ) : (
            districtRows.map((name) => (
              <PlaceRow
                key={name}
                title={name}
                count={overview ? areaCounts[districtKey(name)] || 0 : null}
                onClick={() => go({ district: name })}
              />
            ))
          )}
        </div>
      </AppShell>
    );
  }

  /* ----- Step 3: upazila ----- */
  if (step === "upazila") {
    return (
      <AppShell title="Find blood" back>
        <StepHeader
          step={3}
          blood={blood}
          title={`Where in ${district}?`}
          hint="Narrow it down, or search the whole district."
        />
        <div className="app-list bs-place-list">
          <PlaceRow
            icon={<EnvironmentOutlined />}
            title={`All of ${district}`}
            count={overview ? areaTotal : null}
            onClick={() => go({ upuzila: ALL })}
          />
        </div>
        {placeSearch("Search upazila")}
        <div className="app-list">
          {upazilaRows.length === 0 ? (
            <div className="app-empty">No upazila matches "{placeQuery}"</div>
          ) : (
            upazilaRows.map((name) => (
              <PlaceRow
                key={name}
                title={name}
                count={overview ? areaCounts[name.toLowerCase()] || 0 : null}
                onClick={() => go({ upuzila: name })}
              />
            ))
          )}
        </div>
      </AppShell>
    );
  }

  /* ----- Results ----- */
  const pages = results.data?.pages || [];
  const first = pages[0];
  const donors = pages.flatMap((p) => p.users || []);
  const total = first?.total || 0;
  const readyCount = first?.readyCount || 0;
  const loggedIn = !!first?.loggedIn;
  const place = apiUpazila ? `${apiUpazila}, ${apiDistrict}` : apiDistrict || "Anywhere in Bangladesh";
  const extraGroups = COMPATIBLE_DONORS[blood].filter((g) => g !== blood);

  return (
    <AppShell
      title={`${blood} donors`}
      subtitle={place}
      back
      actions={
        <button type="button" className="app-bar__icon-btn" aria-label="Share this search" onClick={shareSearch}>
          <ShareAltOutlined />
        </button>
      }
    >
      <div className="bs-chips">
        <button type="button" className="bs-chip bs-chip--blood" onClick={() => setGroupSheetOpen(true)}>
          <BloodDropIcon /> {blood} <DownOutlined />
        </button>
        <button type="button" className="bs-chip" onClick={() => go({ district: "", upuzila: "" })}>
          <EnvironmentOutlined /> {apiDistrict || "Anywhere"} <DownOutlined />
        </button>
        {apiDistrict ? (
          <button type="button" className="bs-chip" onClick={() => go({ upuzila: "" })}>
            {apiUpazila || "All upazilas"} <DownOutlined />
          </button>
        ) : null}
      </div>

      <div className="bs-toggles">
        <button
          type="button"
          className={`bs-toggle ${readyOnly ? "bs-toggle--on" : ""}`}
          aria-pressed={readyOnly}
          onClick={() => go({ ready: readyOnly ? "" : "1" }, { replace: true })}
        >
          {readyOnly ? <CheckCircleFilled /> : null} Ready now
        </button>
        {extraGroups.length > 0 ? (
          <button
            type="button"
            className={`bs-toggle ${compatible ? "bs-toggle--on" : ""}`}
            aria-pressed={compatible}
            onClick={() => go({ compat: compatible ? "" : "1" }, { replace: true })}
          >
            {compatible ? <CheckCircleFilled /> : null} Also {extraGroups.join(", ")}
          </button>
        ) : null}
      </div>

      <div className="bs-summary">
        {results.isLoading ? (
          "Searching…"
        ) : (
          <>
            <strong>{plural(total, "donor")}</strong>
            {!readyOnly ? ` · ${readyCount} ready now` : ""}
            {compatible ? (
              <div className="bs-summary__note">
                {blood} patients can also receive {extraGroups.join(", ")}.
              </div>
            ) : null}
          </>
        )}
      </div>

      {!loggedIn && donors.some((d) => d.contactPublic) ? (
        <button type="button" className="bs-login-banner" onClick={openLogin}>
          <LockOutlined />
          <span className="bs-login-banner__text">
            <strong>Log in to call donors.</strong> Phone numbers are only shown to members.
          </span>
          <RightOutlined />
        </button>
      ) : null}

      {results.isError ? <div className="bs-error">Could not load donors. Please try again.</div> : null}

      {results.isLoading ? (
        <div className="bs-list">
          {[0, 1, 2].map((i) => (
            <div key={i} className="bs-card">
              <Skeleton avatar active paragraph={{ rows: 2 }} />
            </div>
          ))}
        </div>
      ) : donors.length === 0 && !results.isError ? (
        <div className="bs-empty">
          <BloodDropIcon className="bs-empty__icon" />
          <p className="bs-empty__title">No {readyOnly ? "ready " : ""}donors here yet</p>
          <div className="bs-empty__actions">
            {readyOnly ? (
              <Button size="large" block onClick={() => go({ ready: "" }, { replace: true })}>
                Include donors who gave recently
              </Button>
            ) : null}
            {!compatible && extraGroups.length > 0 ? (
              <Button size="large" block onClick={() => go({ compat: "1" }, { replace: true })}>
                Also search {extraGroups.join(", ")}
              </Button>
            ) : null}
            {apiUpazila ? (
              <Button size="large" block onClick={() => go({ upuzila: ALL })}>
                Search all of {apiDistrict}
              </Button>
            ) : null}
            {apiDistrict ? (
              <Button size="large" block onClick={() => go({ district: ALL, upuzila: "" })}>
                Search anywhere in Bangladesh
              </Button>
            ) : null}
          </div>
        </div>
      ) : (
        <div className={`bs-list ${results.isFetching && !results.isFetchingNextPage ? "bs-list--loading" : ""}`}>
          {donors.map((donor) => (
            <DonorCard key={donor._id} donor={donor} loggedIn={loggedIn} onLogin={openLogin} />
          ))}
        </div>
      )}

      {results.hasNextPage ? (
        <Button
          className="bs-more"
          size="large"
          block
          loading={results.isFetchingNextPage}
          onClick={() => results.fetchNextPage()}
        >
          Show more donors
        </Button>
      ) : null}

      <Sheet open={groupSheetOpen} onClose={() => setGroupSheetOpen(false)}>
        <h2 className="sheet-form__title" style={{ marginBottom: 16 }}>
          Change blood group
        </h2>
        <GroupGrid
          bloods={bloods}
          selected={blood}
          onSelect={(value) => {
            setGroupSheetOpen(false);
            if (value !== blood) go({ blood: value }, { replace: true });
          }}
        />
      </Sheet>
    </AppShell>
  );
};

export default BloodSearch;
